// DoctorPortalBar — sticky command bar at the top of the patient visit page.
//
// Brett's ask: "edits go under the doctor portal, implement /21st." This
// surfaces the doctor's actual workflow cleanly without rewriting the 9.7k
// lines of legacy Chakra below. It mounts INSIDE .vp-note at the top, so the
// rest of the page is untouched. Patterns are 21st.dev-inspired
// (AI dock + command pill bar + status chip) reimplemented in scoped CSS
// because this project doesn't have shadcn installed.
//
// Actions fan out via window CustomEvents (vmai:doctor-action) so the legacy
// page can listen and react without us threading props through 9k lines.
// Each event has { detail: { action, payload? } }.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getSupabase, supabasePublic } from './supabaseClient';
import { tableName } from './env';
import { getClinicBrand } from '../utils/clinicBrand';
import { getFixtureLabel, isFixtureUuid, fixtureEhrIdForUuid } from '../utils/testPatients';
import { getBackendURL } from './env';
import ClinicVoiceDictate from './ClinicVoiceDictate';
import './DoctorPortalBar.css';

// Per-action keyboard shortcut: tap the letter (no modifier) when nothing
// else is focused. Single-key shortcuts are how Linear / Notion / Superhuman
// keep doctors out of menus.
//
// Bar pare-down history:
//   - 2026-06-27 (Janu dogfood): removed Ask, Forms, Sign — duplicates of
//     existing surfaces (AI Chat bubble, document toolbar, Sign & Save).
//   - 2026-06-28 (Leejoe KISS pass): removed Record and Generate — Record
//     duplicates the floating red mic; Generate duplicates the Process
//     button on the document header. Per the anti-EPIC principle: ONE
//     surface per action.
// Current chips: ICD-10 (pending relocation when ICD catalog ships) + Share
// (will be re-purposed for the per-patient physician-roster routing — see
// pcp-auto-send-spec + upcoming physician-roster spec).
// Also removed in the same pass: the ⌘. Quick-actions command palette
// (redundant with the chips themselves + the rest of the chart toolbar).
const ACTIONS = [
  { key: 'icd',      label: 'ICD-10',   icon: '📑', tone: '',     kbd: 'I', hint: 'Pick problems + auto-build A&P',     aria: 'Pick ICD-10 codes' },
  { key: 'share',    label: 'Share',    icon: '📤', tone: '',     kbd: 'K', hint: 'Share this visit with a colleague',  aria: 'Share visit' },
];

// Task #207: 'empty' status was the meaningless "New visit" chip that
// always showed when the patient was loaded but no recording had started.
// Replaced at render time with the In Visit badge logic — see status
// memoization below. Keeping the rest for real lifecycle states.
const STATUS_BY_FLAG = {
  signed:     { label: 'Signed',    tone: 'done' },
  generated:  { label: 'Draft',     tone: 'ai'   },
  recording:  { label: 'Recording', tone: 'live' },
  recorded:   { label: 'Recorded',  tone: 'warn' },
  empty:      null, // suppress — top bar shows nothing instead of "New visit"
};

function ageToday(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let age = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

function fmtDate(s) {
  if (!s) return '';
  // YYYY-MM-DD strings: parse as LOCAL date, not UTC. new Date("2026-06-27")
  // would interpret as midnight UTC = Fri 5pm PDT = displays "Fri, Jun 26"
  // (off-by-one in any timezone west of UTC). Parsing the parts manually
  // keeps the date in the user's local timezone where the clinic actually
  // sits. Leejoe flagged 2026-06-28: "Saturday Jun 27 patients showing as
  // Friday Jun 26 in the top-right slot."
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function initialsOf(name) {
  if (!name) return '?';
  return name.split(/[\s,]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

// Task #227 + #229 — Reset to Seed chip for the top white bar. Lives next
// to the ICD-10 + Share chips on fixture patients only. UUID-keyed gate
// (Task #229): DoctorPortalBar's own row fetch was returning empty for
// fixtures because public.notes-c is blocked by RLS for the MEDSTUD-
// scoped client, AND supabasePublic() didn't help (same RLS). The chart
// UUID is in the URL — guaranteed available, no fetch needed. We check
// it against the hardcoded FIXTURE_UUIDS list and derive EHR_ID from
// the same map for the reset endpoint body.
function ResetToSeedChip({ uuid, patientName, dateParam }) {
  const [running, setRunning] = useState(false);
  if (!uuid || !isFixtureUuid(uuid)) return null;
  const ehrId = fixtureEhrIdForUuid(uuid) || '';
  const onClick = async () => {
    if (running) return;
    const ok = window.confirm(
      `Reset ${patientName || 'this fixture'} back to original seed state?\n\n` +
      `This wipes any diff already applied and re-pins the pre-clinic note. ` +
      `Audio + reports go back to NEW (re-process them fresh).\n\n` +
      `Only test fixtures can be reset — real patients are protected.`
    );
    if (!ok) return;
    setRunning(true);
    // Leejoe 2026-06-29 — DO NOT hard-reload anymore. The prior approach
    // POSTed to /api/fixtures/reset/ and immediately replaced the page,
    // hoping the cache-buster + Yjs evict would make the next load see
    // clean state. It didn't: the live Yjs collab session + browser
    // IndexedDB resync the dirty in-memory doc and beat the freshly-
    // written .json. Net result was a clickable chip that did nothing.
    //
    // Instead, hand off to MedStudentClinicPage via a window event. The
    // page's `performResetToSeed` handler runs the same POST + then
    // calls editor.setContents(cleanDelta) — exactly the proven path
    // the regular Restore button uses, which bypasses Yjs entirely.
    try {
      window.dispatchEvent(new CustomEvent('vmai:reset-to-seed', {
        detail: { ehrId, dateParam: dateParam || '' },
      }));
    } catch (e) {
      alert(`Reset dispatch error: ${e.message}`);
    } finally {
      // Brief spinner — the page handler shows its own toast on success/fail.
      setTimeout(() => setRunning(false), 1500);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={running}
      title="Test fixture only — wipes any diff and restores original pre-clinic note. Audio + reports survive."
      aria-label="Reset this test fixture to its original seeded state"
      className="dpb-act dpb-reset"
      style={{ opacity: running ? 0.6 : 1, cursor: running ? 'wait' : 'pointer' }}
    >
      <span className="ic" aria-hidden="true">↻</span>
      <span className="lab">{running ? 'Resetting…' : 'Reset to Seed'}</span>
    </button>
  );
}

// Leejoe 2026-06-29 — Record Visit chip. Top-bar red square that opens
// the external recorder popup. Mirrors the floating-mic affordance but
// lives next to the In Visit badge where the doctor's eye is during a
// visit. Dispatches the SAME `recordingBubbleOpen` event the floating
// mic listens for — single source of truth for "open the recorder."
// The mini media-player controls (stop/pause/resume) are deferred to a
// follow-up; this chip ships first so we can confirm the new location
// before adding more surface area.
function RecordVisitChip({ recorderState }) {
  const { recordingState, isRecordingOpen } = recorderState || {};
  const onClick = () => {
    try {
      window.dispatchEvent(new CustomEvent('recordingBubbleOpen', { detail: {} }));
    } catch (_) { /* non-fatal */ }
  };
  // Label + color flips with the recorder's actual state so the chip
  // always tells the doctor what its click will do next.
  let label;
  let bg;
  let title;
  switch (recordingState) {
    case 'recording':
      label = 'Recording…';
      bg = 'linear-gradient(135deg, #dc2626, #991b1b)';
      title = 'Recording in progress — click to bring recorder window to front';
      break;
    case 'paused':
      label = 'Paused';
      bg = 'linear-gradient(135deg, #f97316, #c2410c)';
      title = 'Paused — click to bring recorder window to front';
      break;
    case 'opened':
      label = 'Open Recorder';
      bg = 'linear-gradient(135deg, #ef4444, #b91c1c)';
      title = 'Recorder window is open — click to focus it';
      break;
    case 'stopped':
      label = 'Record Visit';
      bg = 'linear-gradient(135deg, #ef4444, #b91c1c)';
      title = 'Last recording saved — click to start a new one';
      break;
    default:
      label = isRecordingOpen ? 'Open Recorder' : 'Record Visit';
      bg = 'linear-gradient(135deg, #ef4444, #b91c1c)';
      title = isRecordingOpen
        ? 'Recorder window is open — click to focus it'
        : 'Start a new visit recording';
  }
  const pulsing = recordingState === 'recording';
  // Gradient stays JS-driven because it changes with recorder state
  // (idle red vs live red vs paused orange). Other chrome (height,
  // radius, padding, icon+label layout) is now centralized in
  // DoctorPortalBar.css under .dpb-act.dpb-record so the Record +
  // Voice + In Visit cluster shares the same vertical rhythm.
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`dpb-act dpb-record${pulsing ? ' animate-pulse' : ''}`}
      style={{
        background: bg,
        backgroundImage: bg,
      }}
    >
      <svg className="ic" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="12" cy="12" r="6" />
      </svg>
      <span className="lab">{label}</span>
    </button>
  );
}

// Voice Type chip. Top-bar blue mic that opens a small floating dictation
// popup. Lives in the top white bar (always-visible, never blocked by the
// note) so the doctor can dictate without hunting for it. The chip + popup
// are fully self-contained in ClinicVoiceDictate — a clean-room replacement
// for the old VoiceTyping popup, hardened against the busy clinic page's
// frequent re-renders (portal + all-local state + React.memo) so it can't
// flicker. It dispatches transcripts as `vmai:voice-dictate-result` window
// events; MedStudentClinicPage listens and writes them into the editor
// (DoctorPortalBar can't reach the editor ref directly without prop-drilling
// through 9k lines of legacy markup).
function VoiceDictateChip() {
  return <ClinicVoiceDictate />;
}

// Treat the key event's target as "typing" if it's an input, textarea,
// contenteditable, or live within the AI chat field. Single-key shortcuts
// must never fire while the doctor is typing into the note editor.
// `KeyboardEvent.key` is not guaranteed. Browser autofill, password managers,
// IME composition and programmatically dispatched events all produce keydowns
// without it — and because the shortcut listener is bound to `window`, one such
// event crashed the entire page on `e.key.toLowerCase()`. Normalising here
// keeps the handler total: no key means no shortcut match, which is correct.
export function readKey(e) {
  const k = e && e.key;
  return k === undefined || k === null ? '' : String(k);
}

export function isTypingTarget(t) {
  if (!t) return false;
  const tag = (t.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (t.isContentEditable) return true;
  return false;
}

export default function DoctorPortalBar() {
  const { uuid } = useParams();
  const [params] = useSearchParams();
  // Cross-schema chart: the linked-provider schema this patient row lives in
  // (from `?schema=`). Null for a same-schema chart — reads use home as before.
  const chartSchemaParam = params.get('schema');
  const date = params.get('date');
  const navigate = useNavigate();

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [palette, setPalette] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [paletteIdx, setPaletteIdx] = useState(0);
  const [brand, setBrand] = useState(getClinicBrand());
  // Task #207: visit-state mirrored from MedStudentClinicPage via window
  // event so the top bar can render the In Visit badge (blink during active
  // recording, steady when in-visit-but-paused, hidden otherwise).
  const [visitState, setVisitState] = useState({ inVisit: false, recording: false });
  // Leejoe 2026-06-29 — recorder-popup state mirrored via vmai:recording-state
  // (dispatched by RecordingBubble whenever its internal state changes).
  // Drives the new top-bar Record Visit chip's label flip between
  // "Record Visit" / "Recording…" / "Paused" / "Open Recorder".
  const [recorderState, setRecorderState] = useState({
    recordingState: 'closed',
    isRecordingOpen: false,
  });
  const paletteInputRef = React.useRef(null);

  useEffect(() => {
    const onBrand = (e) => setBrand(e.detail || getClinicBrand());
    window.addEventListener('vmai:brand', onBrand);
    return () => window.removeEventListener('vmai:brand', onBrand);
  }, []);

  useEffect(() => {
    const onVisitState = (e) => {
      const next = e?.detail || {};
      setVisitState((prev) => ({
        // Once a session is in-visit, stay in-visit until Sign & Save
        // explicitly clears it (mirrors MedStudentClinicPage behavior).
        inVisit: prev.inVisit || !!next.inVisit,
        recording: !!next.recording,
      }));
      // Sign & Save sends {inVisit:false} explicitly — honor that as a clear.
      if (next.inVisit === false && next.recording === false) {
        setVisitState({ inVisit: false, recording: false });
      }
    };
    window.addEventListener('vmai:visit-state', onVisitState);
    return () => window.removeEventListener('vmai:visit-state', onVisitState);
  }, []);

  useEffect(() => {
    const onRecorderState = (e) => {
      const d = e?.detail || {};
      setRecorderState({
        recordingState: d.recordingState || 'closed',
        isRecordingOpen: !!d.isRecordingOpen,
      });
    };
    window.addEventListener('vmai:recording-state', onRecorderState);
    return () => window.removeEventListener('vmai:recording-state', onRecorderState);
  }, []);

  // Pull the visit row — independent fetch keeps this component decoupled
  // from PatientPage's internal state machine. Cheap one-shot read.
  //
  // 2026-06-29 (Leejoe debug): try the tenant-scoped client first (where
  // production patients live), then fall back to the public-schema client
  // (where the FOOTHILL/KENJI fixtures live). Without the fallback the
  // fixture-only Reset to Seed chip never renders for medstudent-role
  // viewers because `getSupabase()` pins MEDSTUD schema and the fixtures
  // aren't there.
  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    // `*` rather than a named list: tenant notes-c schemas drift (e.g.
    // `signed_at` is missing in some), and PostgREST rejects the WHOLE query
    // with a 400 when any named column is absent — which silently blanked
    // this bar's status row on those tenants. Star-select returns whatever
    // exists and every consumer already tolerates absent fields.
    const SELECT_COLS = '*';
    (async () => {
      try {
        const sb = await getSupabase();
        // Cross-schema chart: honor `?schema=` so a linked-provider patient's
        // status/EHR reads hit ITS schema, not the viewer's home (which 400s on
        // tenant column drift / 406s when the row simply isn't in home).
        const primary = chartSchemaParam ? sb.schema(chartSchemaParam) : sb;
        const { data, error } = await primary
          .from(tableName)
          .select(SELECT_COLS)
          .eq('id', uuid)
          .single();
        if (cancelled) return;
        if (!error && data) {
          setRow(data);
          setLoading(false);
          return;
        }
        // Tenant-scoped fetch missed (likely a public-schema fixture
        // patient viewed by a medstudent-role user whose getSupabase()
        // pins MEDSTUD). Try the public-schema client. Same query, no
        // schema header — PostgREST defaults to public.
        try {
          const sbPub = supabasePublic();
          const { data: pubData, error: pubError } = await sbPub
            .from(tableName)
            .select(SELECT_COLS)
            .eq('id', uuid)
            .single();
          if (!cancelled && !pubError && pubData) setRow(pubData);
        } catch (_) { /* swallow — row stays null, chip stays hidden */ }
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uuid, chartSchemaParam]);

  // Filtered + ranked palette items. Fuzzy match against label + hint.
  const paletteItems = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    if (!q) return ACTIONS;
    return ACTIONS
      .map((a) => {
        const hay = `${a.label} ${a.hint}`.toLowerCase();
        let score = 0;
        if (a.label.toLowerCase().startsWith(q)) score += 3;
        if (hay.includes(q)) score += 2;
        if (a.kbd.toLowerCase() === q) score += 5;
        return { ...a, _score: score };
      })
      .filter((a) => a._score > 0)
      .sort((a, b) => b._score - a._score);
  }, [paletteQuery]);

  // Reset palette state on close + focus the input on open.
  useEffect(() => {
    if (palette) {
      setPaletteQuery('');
      setPaletteIdx(0);
      // Focus next tick so the dialog has time to render.
      const t = setTimeout(() => paletteInputRef.current?.focus(), 16);
      return () => clearTimeout(t);
    }
  }, [palette]);

  // Global keyboard: ⌘. (or Ctrl-.) toggles palette; single-letter shortcuts
  // fire actions when nothing else is focused; arrow keys + Enter navigate
  // inside the palette; Esc closes everything.
  useEffect(() => {
    const onKey = (e) => {
      const k = readKey(e);
      if (!k) return;   // autofill and friends — nothing to match
      const klc = k.toLowerCase();
      // Palette toggle — always available, even mid-typing
      if ((e.metaKey || e.ctrlKey) && k === '.') { e.preventDefault(); setPalette((p) => !p); return; }
      if (k === 'Escape') { setPalette(false); return; }

      if (palette) {
        // Inside the palette: arrow keys + Enter
        const max = Math.max(0, paletteItems.length - 1);
        if (k === 'ArrowDown') { e.preventDefault(); setPaletteIdx((i) => Math.min(max, i + 1)); return; }
        if (k === 'ArrowUp')   { e.preventDefault(); setPaletteIdx((i) => Math.max(0, i - 1)); return; }
        if (k === 'Enter')     { e.preventDefault(); const it = paletteItems[paletteIdx]; if (it) fireRef.current?.(it.key); return; }
        return; // Palette swallows all other input
      }

      // Single-key shortcuts only when nothing is being typed into.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const match = ACTIONS.find((a) => a.kbd.toLowerCase() === klc);
      if (match) { e.preventDefault(); fireRef.current?.(match.key); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [palette, paletteItems, paletteIdx]);

  // Hold `fire` in a ref so the keyboard effect doesn't need to re-attach
  // on every render (and avoids stale closures over the navigate fn).
  const fireRef = React.useRef(null);

  const status = useMemo(() => {
    if (!row) return STATUS_BY_FLAG.empty;
    if (row.signed_at || row.finalized) return STATUS_BY_FLAG.signed;
    if (row.audio_url) return STATUS_BY_FLAG.recorded;
    return STATUS_BY_FLAG.empty;
  }, [row]);

  const fire = useCallback((action, payload) => {
    setPalette(false);
    try {
      window.dispatchEvent(new CustomEvent('vmai:doctor-action', { detail: { action, payload, visitId: uuid } }));
    } catch (e) { /* ignore */ }
    // Wire a few actions to obvious in-app destinations as a useful fallback
    // until the legacy page wires its CustomEvent listeners.
    if (action === 'forms') navigate('/forms');
    if (action === 'ask') {
      // Best-effort: click the global AI floating bubble if it's mounted.
      const el = document.querySelector('[data-vmai-ai-bubble]') || document.querySelector('.ChatBubble, .chat-bubble');
      if (el && typeof el.click === 'function') el.click();
    }
    if (action === 'record') {
      const el = document.querySelector('[data-vmai-record-bubble]') || document.querySelector('.RecordingBubble, .recording-bubble');
      if (el && typeof el.click === 'function') el.click();
    }
  }, [navigate, uuid]);

  // Keep the keyboard handler's ref pointed at the latest fire callback.
  useEffect(() => { fireRef.current = fire; }, [fire]);

  const age = row && ageToday(row.dob);
  const visitDate = fmtDate(date || row?.date_);
  // 2026-06-28 (Leejoe): always show clinic name + date in the top slot,
  // not the patient name. Rationale:
  //   - Patient name + DOB/MRN already appear in PatientHeaderBar
  //     immediately below this strip (zero info gain to duplicate)
  //   - Clinic + date is the ONE thing always-useful for context:
  //     consultants float between clinics, off-day chart browsing
  //     doesn't have a "today's patient", multi-physician clinics can't
  //     pin to one doctor
  //
  // 2026-06-28 followup (Leejoe): brand?.name was returning null so the
  // label was showing "Today" — should hardcode "Foothill Pulmonary" as
  // the fallback until per-clinic brand config lands (it's the only
  // active pilot clinic). Future: read from clinic_settings table.
  const clinicName = brand?.name || 'Foothill Pulmonary';
  // 2026-06-28 (Leejoe): for seeded TEST PATIENTS, the top-bar swaps the
  // clinic+date pair for a fixture descriptor — "Foothill Pulmonary test
  // follow up sleep patient". The word "test" is the at-a-glance signal
  // that this isn't a real chart, and the encounter+subspecialty descriptor
  // tells the dogfooder/demo audience what kind of test case they're in.
  // Production patients fall through to clinic + date as before.
  // Task #229 — same UUID fallback as the Reset chip. row.EHR_ID is empty
  // for fixture patients (RLS blocks public.notes-c reads from MEDSTUD-
  // scoped users), so fall through to UUID lookup so the descriptor still
  // renders ("test follow up sleep patient" instead of generic clinic+date).
  const effectiveEhrId = row?.EHR_ID || fixtureEhrIdForUuid(uuid);
  const fixtureLabel = getFixtureLabel(effectiveEhrId, clinicName);
  const headerLabel = fixtureLabel || (clinicName + (visitDate ? ` · ${visitDate}` : ''));
  const patient = row?.patient_name || (loading ? '…' : headerLabel);
  const topBarLabel = headerLabel;
  const topBarSubtitle = fixtureLabel
    ? 'Test fixture'
    : (brand?.name && visitDate ? (row?.type ? `${row.type}` : 'Clinic day') : '');

  return (
    <>
      <div className="dpb" role="toolbar" aria-label="Doctor portal actions">
        <div className="dpb-left">
          {brand?.logo ? (
            <img className="dpb-brand" src={brand.logo} alt="" />
          ) : (
            <div className="dpb-brand-fallback">✦</div>
          )}
          <div className="dpb-pat">
            <span className="dpb-pav">{initialsOf(topBarLabel)}</span>
            <div className="dpb-pmeta">
              <div className="dpb-pn">{topBarLabel}</div>
              {topBarSubtitle && <div className="dpb-psub"><span>{topBarSubtitle}</span></div>}
            </div>
          </div>
        </div>

        <div className="dpb-mid">
          {/* Leejoe 2026-06-29 — Record Visit chip lives LEFT of the
              In Visit badge. Replaces the floating red mic at the
              bottom (kept in place for one validation cycle so the
              new chip can be confirmed before retiring the old). The
              chip dispatches `recordingBubbleOpen` — same event the
              floating mic listens for — so a single click here is
              equivalent to clicking the floating mic: opens the
              external recorder popup, or focuses it if already open.
              Label flips with recordingState so the doctor always
              sees the current next-action ("Record Visit" when idle,
              "Recording…" while live, "Paused" when paused, "Open
              Recorder" when window is open but no audio yet). */}
          <RecordVisitChip recorderState={recorderState} />
          {/* Task #207: prioritize In Visit badge over the lifecycle status
              chip — the In Visit signal is the most useful "where are we"
              indicator for both clinician and observer (Ponnezhan demo).
              Blinks during active recording, steady when in-visit-but-paused.
              Falls through to the legacy status chip ONLY for real lifecycle
              states (Recording/Recorded/Draft/Signed); the meaningless
              "New visit" empty state renders nothing. */}
          {visitState.inVisit ? (
            <span
              className="dpb-status live"
              role="status"
              aria-live="polite"
              aria-label={visitState.recording ? 'Recording in progress' : 'Visit in progress'}
            >
              <span className="dot" aria-hidden="true">
                {visitState.recording && <span className="ping" aria-hidden="true" />}
              </span>
              {visitState.recording ? 'Recording' : 'In Visit'}
            </span>
          ) : status ? (
            <span
              className={`dpb-status ${status.tone}`}
              role="status"
              aria-live="polite"
              aria-label={`Visit status: ${status.label}`}
            >
              <span className="dot" aria-hidden="true">
                <span className="ping" aria-hidden="true" />
              </span>
              {status.label}
            </span>
          ) : null}
          {/* Leejoe 2026-06-29 — Voice Dictate chip lives RIGHT of the
              In Visit badge so the three-up layout reads:
                [Record Visit]  [In Visit]  [Voice Dictate]
              Always-on, never blocks the note (top bar is fixed). */}
          <VoiceDictateChip />
          {/* Combine Documents moved into the document Tools menu
              (PatientHeaderBar → DocumentToolsMenu, 2026-07-05). */}
        </div>

        <div className="dpb-right">
          {/* Task #227 — Reset to Seed chip lives in the top white bar
              (not the patient action row) and ONLY for fixture patients.
              Gating moved INSIDE the chip itself so a transient null row
              during load doesn't drop the chip permanently. The chip
              returns null when not a fixture, renders its orange pill
              otherwise. */}
          <ResetToSeedChip uuid={uuid} patientName={row?.patient_name} dateParam={date} />
          {ACTIONS.map((a) => (
            <button
              key={a.key}
              className={`dpb-act ${a.tone}`}
              title={`${a.hint} · press ${a.kbd}`}
              aria-label={a.aria}
              aria-keyshortcuts={a.kbd}
              onClick={() => fire(a.key)}
            >
              <span className="ic" aria-hidden="true">{a.icon}</span>
              <span className="lab">{a.label}</span>
              <span className="dpb-actkbd" aria-hidden="true">{a.kbd}</span>
            </button>
          ))}
        </div>
      </div>

      {palette && (
        <div className="dpb-scrim" onClick={() => setPalette(false)}>
          <div
            className="dpb-cmd"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Doctor portal quick actions"
          >
            <div className="dpb-cmdhead">
              <span className="dpb-cmdeyebrow">Doctor portal · {patient}</span>
              <div className="dpb-cmdsearchwrap">
                <span className="dpb-cmdsearchicon" aria-hidden="true">⌕</span>
                <input
                  ref={paletteInputRef}
                  className="dpb-cmdsearch"
                  type="text"
                  placeholder="Type a command, search, or press a letter…"
                  value={paletteQuery}
                  onChange={(e) => { setPaletteQuery(e.target.value); setPaletteIdx(0); }}
                  aria-label="Search actions"
                  aria-controls="dpb-cmdlist"
                  autoComplete="off"
                  spellCheck="false"
                />
              </div>
            </div>
            <ul id="dpb-cmdlist" className="dpb-cmdlist" role="listbox" aria-label="Actions">
              {paletteItems.length === 0 && (
                <li className="dpb-cmdempty">No match. Try different words.</li>
              )}
              {paletteItems.map((a, i) => (
                <li
                  key={a.key}
                  role="option"
                  aria-selected={i === paletteIdx}
                  className={`dpb-cmditem ${a.tone}${i === paletteIdx ? ' on' : ''}`}
                  onMouseEnter={() => setPaletteIdx(i)}
                  onClick={() => fire(a.key)}
                >
                  <span className="ic" aria-hidden="true">{a.icon}</span>
                  <div className="t">
                    <b>{a.label}</b>
                    <em>{a.hint}</em>
                  </div>
                  <span className="dpb-cmdkbd" aria-hidden="true">{a.kbd}</span>
                </li>
              ))}
            </ul>
            <div className="dpb-cmdfoot">
              <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
              <span><kbd>↵</kbd> select</span>
              <span><kbd>esc</kbd> close</span>
              <span className="grow" />
              <span className="dim">⌘. toggles</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
